/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { useEffect, useState } from "react";
import { MdArrowDropDown } from "react-icons/md";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import axiosWrapper from "@AxiosWrapper";
import type {
  DatabankViewData,
  GetDataBankViewResponse,
  QueriesDataResponse,
} from "@ServerTypes/queries/queries.schema";
import { setDatabankData } from "@Reducers/Databank";
import { RootState } from "@Reducers/index";

/** Saved-view payload applied to the grid (query/columns/grouping/conditions). */
export type ViewChangePayload = {
  query: string;
  columns: string[];
  group: string[];
  sortConditions?: unknown[];
  filterConditions?: Record<string, unknown>;
  groupConditions?: unknown[];
};

/**
 * `databankViewDataSchema` declares its own recursion as `z.lazy((): z.ZodType => …)`, so the stored
 * `views` map erases to `Record<string, unknown>`. Every writer (this file and SearchSet1) stores a
 * `DatabankViewData`, so the map is narrowed through this guard rather than an assertion.
 */
const isViewMap = (value: unknown): value is Record<string, DatabankViewData> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Props of the saved-views dropdown. `id` + `fileVersionId` key the slice in the Databank reducer. */
type ViewsProps = {
  fileVersionId: string;
  id: string;
  onChange: (view: ViewChangePayload) => void;
};

function Views({ fileVersionId, id, onChange }: ViewsProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const DefaultViewName = "Default View";
  const DefaultViews = {
    [DefaultViewName]: {
      query: "",
      columns: [],
    },
  };
  const filesData = useSelector((state: RootState) => state.filesData);
  const {
    viewsLoaded,
    views: storedViews,
    selectedView = DefaultViewName,
    updateCurrentView = false,
    // The `?? {}` sits OUTSIDE the selector on purpose: inside, it allocated a fresh object on every
    // call and this component re-rendered on every dispatched action. The selector now returns the
    // stored reference (or undefined), which is a stable identity.
    //
    // `updateCurrentView` is a client-only key of the slice that `databankViewDataSchema` does not
    // declare (its own description says so).
  } = (useSelector((state: RootState) => state.Databank[id]?.[fileVersionId]) ?? {}) as DatabankViewData & {
    updateCurrentView?: boolean;
  };
  const views: Record<string, DatabankViewData> = isViewMap(storedViews) ? storedViews : {};

  const [showViewDropdown, setShowViewDropdown] = useState(false);

  const handleViewDropdownClick = () => {
    setShowViewDropdown((showViewDropdown) => !showViewDropdown);
  };

  const init = async () => {
    const response = await axiosWrapper.post<GetDataBankViewResponse>(`${getBackendUrl()}/queries/getDataBankView`, {
      fileId: filesData[fileVersionId]?.fileId,
    });
    const data = response.data.data;
    if (data) {
      // Support new format { views, selectedView } and old format { [viewName]: { ... } }
      // The old format stores the view map directly on `data`, which the response schema does not
      // describe, hence the widening intersection on that branch.
      const newFormatViews = isViewMap(data.views) ? data.views : null;
      const viewsData: Record<string, DatabankViewData> =
        newFormatViews ?? (data as typeof data & Record<string, DatabankViewData>);
      const lastSelectedView: string = newFormatViews ? data.selectedView || DefaultViewName : DefaultViewName;
      const firstViewName: string = Object.keys(viewsData)[0] || DefaultViewName;
      const restoredSelectedView: string = viewsData[lastSelectedView] ? lastSelectedView : firstViewName;
      const selectedViewData: DatabankViewData = viewsData[restoredSelectedView] || {};
      dispatch(
        setDatabankData({
          data: {
            views: viewsData,
            selectedView: restoredSelectedView,
            filteredColumns: selectedViewData.columns || [],
            queryId: selectedViewData.query || "",
            // Falls back to `[]`: `group` is `string[] | undefined` and every consumer (and
            // DatabankViewData) treats it as an array. It used to fall back to `""`.
            group: selectedViewData.group ?? [],
            viewsLoaded: true,
          },
          fileVersionId,
          id,
        }),
      );
    } else {
      dispatch(
        setDatabankData({
          data: {
            views: DefaultViews,
            selectedView: DefaultViewName,
            filteredColumns: [],
            queryId: "",
            viewsLoaded: true,
          },
          fileVersionId,
          id,
        }),
      );
    }
  };

  useEffect(() => {
    init();
  }, []);

  const saveView = async (
    fileId: string | undefined,
    view: { views?: Record<string, DatabankViewData>; selectedView?: string },
  ) => {
    await axiosWrapper.post<QueriesDataResponse>(`${getBackendUrl()}/queries/saveDataBankView`, {
      fileId,
      dataBankViews: view,
    });
  };

  const handleUpdateView = async () => {
    await saveView(filesData[fileVersionId]?.fileId, { views, selectedView });
  };

  useEffect(() => {
    if (viewsLoaded) {
      handleUpdateView();
    }
  }, [views]);

  useEffect(() => {
    if (viewsLoaded) {
      handleUpdateView();
    }
  }, [selectedView]);

  useEffect(() => {
    if (updateCurrentView) {
      handleUpdateView();
    }
  }, [updateCurrentView]);

  const handleCreateNewView = async () => {
    const tempViewData: Record<string, DatabankViewData> = { ...views };
    const currentView: DatabankViewData = tempViewData[selectedView] || {};
    tempViewData[`View ${Object.keys(tempViewData).length + 1}`] = {
      columns: currentView.columns,
      query: currentView.query,
      group: currentView.group,
      sortConditions: currentView.sortConditions,
      filterConditions: currentView.filterConditions,
      groupConditions: currentView.groupConditions,
    };

    await saveView(filesData[fileVersionId]?.fileId, { views: tempViewData, selectedView });

    dispatch(
      setDatabankData({
        data: {
          views: tempViewData,
        },
        fileVersionId,
        id,
      }),
    );
  };

  const handleViewSelection = (selectedName: string) => {
    dispatch(
      setDatabankData({
        data: {
          selectedView: selectedName,
        },
        fileVersionId,
        id,
      }),
    );

    setShowViewDropdown(false);
    // The dropdown only lists keys of `views`; the per-field fallbacks mirror what the consumer
    // (SearchSet1's `initializeView`) already defaults each of them to.
    const view = views[selectedName];
    onChange({
      query: view?.query ?? "",
      columns: view?.columns ?? [],
      group: view?.group ?? [],
      sortConditions: view?.sortConditions,
      filterConditions: view?.filterConditions,
      groupConditions: view?.groupConditions,
    });
  };

  return (
    <div>
      {/* CUBE-30146: reserve an unshrinkable slot for the caret and clamp/truncate the label, so a
          long view name can no longer run under the arrow or push the item past its toolbar slot. */}
      <div
        className="flex max-w-40 cursor-pointer items-center gap-1"
        onClick={handleViewDropdownClick}
        title={selectedView}
      >
        <span className="truncate">{selectedView}</span>
        <MdArrowDropDown className="shrink-0" size="1.5rem" />
      </div>
      {showViewDropdown && (
        <div className="fixed z-[10] flex flex-col gap-4 bg-white p-5 shadow-[0.25rem_0.5rem_1.25rem_0_rgba(0,0,0,0.3)]">
          <div className="flex flex-col gap-4">
            {Object.keys(views).map((data: string, index: number) => (
              <div onClick={() => handleViewSelection(data)} key={index} data-name={data}>
                {data}
              </div>
            ))}
          </div>
          <div>
            <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleCreateNewView}>
              {t("Create View")}
            </ButtonComponent>
          </div>
        </div>
      )}
    </div>
  );
}

export default Views;
